import { gql } from '@reverbdotcom/commons/src/gql';
import * as React from 'react';
import classNames from 'classnames';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import CreateOfferButton from '@reverbdotcom/commons/src/offers/create_offer_button';
import { BumpKeyFragment, ListViewListings, arbiter_Interaction_InteractionType } from '@reverbdotcom/commons/src/gql/graphql';
import { SHOP_PATH, isOutletHub } from '@reverbdotcom/commons/src/url_helpers';
import AddToCartButton from '@reverbdotcom/commons/src/components/add_to_cart_button';
import { DisplayStyle, WatchBadge } from '@reverbdotcom/commons/src/components/watch_badge';
import { WatchParentComponentNames } from '@reverbdotcom/commons/src/event_tracking/watchlist';

import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { SOLD_LISTING_STATES, WEB } from '@reverbdotcom/commons/src/constants';
import { buildListingClickEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ListingWithSignals } from '@reverbdotcom/commons/src/components/listing_card_signals';
import { withRouter, WithRouterProps } from 'react-router';
import { RCListingRowCard } from '@reverbdotcom/cadence/components/RCListingRowCard/RCListingRowCard';
import ImageCarousel from '@reverbdotcom/commons/src/components/image_carousel';
import {
  buildListingForBuyer,
  getEyebrow,
  buildPrice,
  getRCListingCardNudges,
  isListingDomesticToBuyer,
  useSoldPriceRecordsQuery,
} from '@reverbdotcom/commons/src/listing_helpers';
import ListingCardTag from '@reverbdotcom/commons/src/components/listing_card_tag';
import { ListingCardMetadata } from '@reverbdotcom/commons/src/components/listing_card_metadata';
import { ListingTitleHtmlTags } from '@reverbdotcom/cadence/components/types';
import { ListingCardReviews } from '@reverbdotcom/commons/src/components/reviews_metadata';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { useBumpInteractionTracking } from '@reverbdotcom/commons/src/trackBumpInteraction';

const COMPONENT_NAME = 'ListViewRowCard';
const LIVE = 'live';
const DISPLAY_STYLE = 'Row';

export interface ExternalProps {
  listing: ListViewListings.Fragment & ListingWithSignals & BumpKeyFragment;
  loading: boolean;
  position?: number;
  listingsCount?: number;
  cmsComponentId?: string;
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  handleCreateWatchResult?: () => void;
  showFlag?: boolean;
  hideMetadataAsOffer?: boolean;
  includeItemListMetadata?: boolean;
  displayGreatValue?: boolean;
  titleHtmlTag?: keyof typeof ListingTitleHtmlTags;
  reviews?: ListingCardReviews;
  enableSwiper?: boolean;
  isCPRollupWinner?: boolean;
}

type IProps = ExternalProps & WithRouterProps;

export function ListViewRowCard({ listing, ...props }: IProps) {
  const user = useUser();
  const bumpInteractionTracking = useBumpInteractionTracking({
    interactionType: arbiter_Interaction_InteractionType.CLICK,
    bumpKey: listing?.bumpKey,
    listingId: listing?.id,
  });
  const isSoldListing = SOLD_LISTING_STATES.includes(listing?.state);
  const priceRecordsResult = useSoldPriceRecordsQuery(listing, isSoldListing);

  if (props.loading) {
    return <RCListingRowCard.Skeleton />;
  }

  const listingClickEvent = () => {
    const sellerCountryCode = listing.shop.address?.country.countryCode;

    const trackedExperiments = [
      // experiment.NAME # Add experiments here
    ];

    return {
      experiments: formatExperimentsForEventAttributes(
        user,
        trackedExperiments,
      ),
      query: props.trackingQuery,
      filter: props.trackingFilter,
      sort: props.trackingSort,
      page: props.trackingPage,
      ...buildListingClickEvent({
        listing: listing,
        componentName: COMPONENT_NAME,
        cmsComponentId: props.cmsComponentId,
        displayStyle: DISPLAY_STYLE,
        position: props.position,
        listingsCount: props.listingsCount,
        city: listing.shop.address?.locality,
        region: listing.shop.address?.region,
        countryCode: sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(sellerCountryCode, user?.countryCode),
      }),
    };
  };

  const receivedCount = listing.seller?.feedbackSummary?.receivedCount;
  const rollingRatingPercentage = listing.seller?.feedbackSummary?.rollingRatingPercentage;
  const rating = rollingRatingPercentage / 100;
  const openNewTab = user && user.deviceName === WEB;
  const images = listing.images.map(i => (i.source));
  const combinedShippingDiscount = false;
  const showShippingDisplay = true;
  const decoratedListing = buildListingForBuyer(
    listing,
    { combinedDiscountActive: combinedShippingDiscount },
  );
  const soldPrice = priceRecordsResult?.data?.priceRecordsSearch?.priceRecords?.[0]?.amountProduct?.display;
  const price = buildPrice(decoratedListing, combinedShippingDiscount, showShippingDisplay, isSoldListing, soldPrice);
  const nudges = getRCListingCardNudges({
    listing,
    decoratedListing,
    shop: listing.shop,
    displayGreatValue: props?.displayGreatValue,
    user,
    showFlag: props?.showFlag,
    location: props?.location,
  });

  return (
    <div
      className={classNames(
        {
          'list-view-row-card--loading': props.loading,
        },
      )}
      itemProp={props.includeItemListMetadata ? 'itemListElement' : null}
      itemScope={props.includeItemListMetadata}
      itemType={props.includeItemListMetadata && 'http://schema.org/Product'}
    >
      {props.includeItemListMetadata &&
        <ListingCardMetadata
          listing={listing}
          hideMetadataAsOffer={props.hideMetadataAsOffer}
          includeItemListMetadata={props.includeItemListMetadata}
          reviews={props.reviews}
        />
      }
      <RCListingRowCard
        thumbnailElement={(
          <ImageCarousel
            images={images}
            bumpInteractionTracking={bumpInteractionTracking}
            parentComponentName={COMPONENT_NAME}
            enableSwiper={props.enableSwiper}
            navigateToUrl={decoratedListing.listingUrl}
            clickEvent={listingClickEvent()}
            swiperAriaLabel={listing.title}
          />
        )}
        title={listing.title}
        titleHtmlTag={props.titleHtmlTag}
        conditionLabel={decoratedListing.conditionLabel}
        price={price}
        nudges={nudges}
        eyebrow={getEyebrow(listing, true)}
        violator={!isOutletHub(props?.location) && <ListingCardTag listing={listing} />}
        linkElement={(children) => (
          <CoreLink
            to={decoratedListing.listingUrl}
            target={openNewTab ? '_blank' : undefined}
            clickEvent={listingClickEvent()}
          >
            {children}
          </CoreLink>
        )}
        shopInfo={{
          location: listing.shop.address.displayLocation,
          name: listing.shop.name,
          rating: {
            count: receivedCount,
            rating: rating,
          },
          linkElement: (children) => (
            <CoreLink
              to={SHOP_PATH.expand({ slug: listing.shop.slug })}
              target={openNewTab ? '_blank' : undefined}
            >
              {children}
            </CoreLink>
          ),
        }}
        primaryAction={listing.state === LIVE && (
          <AddToCartButton
            listing={listing}
            parentComponentName={COMPONENT_NAME}
            listingClickEvent={listingClickEvent()}
          />
        )}
        secondaryAction={<CreateOfferButton
          className="list-view-row-card__make-offer"
          listing={listing}
          parentComponentName={COMPONENT_NAME}
        />}
        tertiaryAction={<WatchBadge
          listing={listing}
          parentComponentName={WatchParentComponentNames.MarketplaceListViewAction}
          useToast
        />}
        favoriteButtonElement={(
          <WatchBadge
            listing={listing}
            displayStyle={DisplayStyle.ICON_BUTTON}
            iconButtonSize="mini"
            parentComponentName={WatchParentComponentNames.MarketplaceListViewIcon}
            handleCreateWatchResult={props.handleCreateWatchResult}
            useToast
          />
        )}
      />
    </div>
  );
}

export const ListViewRowCardFragment = gql(`
  fragment ListViewListings on Listing {
    _id
    id
    categoryUuids
    state
    shop {
      _id
      name
      slug
      preferredSeller
      quickShipper
      quickResponder
      address {
        _id
        locality
        region
        displayLocation
        country {
          _id
          countryCode
          name
        }
      }
    }
    seller {
      _id
      feedbackSummary {
        receivedCount
        rollingRatingPercentage
      }
    }
    csp {
      _id
      webLink {
        href
      }
    }
    ... AddToCartButtonFields
    ... ListingCardFields
    ... ListingCreateOfferButtonData
    ... InOtherCartsCardData
  }
`);

export default withRouter<ExternalProps>(ListViewRowCard);
